<template>
	<div class="pt-8">
		<div class="text-center text-ihh-midnight-gray text-3xl lg:text-4xl font-semibold tracking-widest mb-14">Popular Products</div>

		<div class="custom-scroller-container relative w-full max-w-8xl mx-auto">
			<!-- Left Arrow Button -->
			<button
				aria-label="Scroll left"
				@click="back"
				class="rounded-lg bg-ihh-black/70 px-3 py-3 absolute left-0 z-10 top-1/2 transform -translate-y-1/2 text-white stroke-ihh-white rotate-180"
				:class="{ 'opacity-30': atStart }"
				:disabled="atStart"
			>
				<svg class="w-3" viewBox="0 0 9 13" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 1L7 6.5L1 12" stroke-width="1.75" fill="none" />
				</svg>
			</button>

			<!-- Content Area -->
			<div class="mx-12 overflow-hidden">
				<div
					class="scroll-container flex transition-all duration-500 ease-in-out"
					ref="scrollContainer"
					:style="{ transform: `translateX(-${currentIndex * (100 / visibleItems)}%)` }"
				>
					<div
						v-for="(p, index) in popular"
						:key="p.id"
						class="product-slide flex-shrink-0 px-3"
						:class="{
							'w-full': visibleItems === 1,
							'w-1/2': visibleItems === 2,
							'w-1/3': visibleItems === 3,
							'w-1/4': visibleItems === 4,
							'w-1/5': visibleItems === 5,
						}"
					>
						<router-link
							:to="`/products/${p?.customUrl?.url?.replace('/', '')?.trim() || p.id}`"
							class="relative rounded-xl block w-full product-item transition-all duration-300 ease-in-out bg-white"
						>
							<div class="cursor-pointer product-image-container overflow-hidden">
								<img
									:src="p.images?.find((i) => i.isThumbnail)?.url || p.images?.[0]?.url"
									:alt="p?.alt"
									class="object-center object-contain w-full aspect-square transition-transform duration-300 ease-in-out"
								/>
							</div>
							<div class="mt-4 flex w-full justify-center text-center text-sm md:text-xl font-semibold px-4 rounded py-px tracking-wide text-ihh-midnight-gray">
								{{ p.name }}
							</div>
							<div class="text-base text-ihh-gray-200 flex text-center w-full justify-center px-4 my-2 pb-3"></div>
						</router-link>
					</div>
				</div>
			</div>

			<!-- Right Arrow Button -->
			<button
				aria-label="Scroll right"
				@click="next"
				class="rounded-lg bg-ihh-black/70 px-3 py-3 absolute right-0 z-10 top-1/2 transform -translate-y-1/2 text-white stroke-ihh-white"
				:class="{ 'opacity-30 disabled:cursor-not-allowed': atEnd }"
				:disabled="atEnd"
			>
				<svg class="w-3" viewBox="0 0 9 13" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 1L7 6.5L1 12" stroke-width="1.75" fill="none" />
				</svg>
			</button>
		</div>
	</div>
</template>

<script>
import Mango from '../../../helpers/mango'

export default {
	data() {
		return {
			popular: [],
			oldPopularBcIds: [862, 1314, 551, 630, 2022, 3340, 3866, 3912, 3916],
			popularBcIds: [551, 862, 1314, 2022, 4185, 3338, 2042, 779, 942],
			currentIndex: 0,
			visibleItems: 2, // Default to mobile view
			atStart: true,
			atEnd: false,
			resizeObserver: null,
			scrollPosition: 0,
			slideWidth: 0,
			itemGap: 24, // 2 * px-3 padding (6px * 2 = 12px per side)
		}
	},
	async created() {
		let fields = ['id', 'name', 'customUrl', 'images', 'alt', 'rating', 'bcId', 'sku', 'popular', 'popularOrder']
		let response = await Mango.products({ search: { popular: true }, limit: 20, fields })
		response.sort((a, b) => a.popularOrder - b.popularOrder)
		this.popular = response
	},
	mounted() {
		// Initial update of visible items based on screen width
		this.updateVisibleItems()

		// Use a short timeout to ensure the DOM is fully rendered
		setTimeout(() => {
			this.updateNavigation()
		}, 100)

		// Setup resize observer to update visible items on window resize
		this.resizeObserver = new ResizeObserver(() => {
			this.updateVisibleItems()

			// Reset to first item if we've scrolled too far after resize
			if (this.currentIndex > 0 && this.currentIndex + this.visibleItems > this.popular.length) {
				this.currentIndex = Math.max(0, this.popular.length - this.visibleItems)
			}

			this.updateNavigation()
		})
		this.resizeObserver.observe(document.documentElement)

		// Watch for image loads to recalculate dimensions
		this.$nextTick(() => {
			const images = this.$el.querySelectorAll('img')
			images.forEach((img) => {
				img.addEventListener('load', () => {
					this.updateNavigation()
				})
			})
		})
	},
	beforeUnmount() {
		// Clean up resize observer
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
		}
	},
	methods: {
		next() {
			if (this.atEnd) return

			// Just advance one screen worth of items
			this.currentIndex = Math.min(this.popular.length - this.visibleItems, this.currentIndex + this.visibleItems)

			this.$nextTick(() => {
				this.updateNavigation()
			})
		},
		back() {
			if (this.atStart) return

			// Just go back one screen worth of items
			this.currentIndex = Math.max(0, this.currentIndex - this.visibleItems)

			this.$nextTick(() => {
				this.updateNavigation()
			})
		},
		updateVisibleItems() {
			// Determine how many items to show based on screen width
			const width = window.innerWidth

			if (width >= 1280) {
				this.visibleItems = 5 // xl: 5 items
			} else if (width >= 1024) {
				this.visibleItems = 4 // lg: 4 items
			} else if (width >= 768) {
				this.visibleItems = 3 // md: 3 items
			} else if (width >= 560) {
				this.visibleItems = 2 // sm2: 2 items
			} else {
				this.visibleItems = 1 // below sm2: 1 item
			}
		},
		updateNavigation() {
			if (!this.$refs.scrollContainer) return

			// Update start/end states
			this.atStart = this.currentIndex <= 0
			this.atEnd = this.currentIndex + this.visibleItems >= this.popular.length
		},
	},
}
</script>

<style scoped>
/* Product item styles */
.product-item {
	transform: translateY(0);
	transition: all 0.3s ease;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.product-item:hover {
	transform: translateY(-6px);
	box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.product-image-container {
	overflow: hidden;
	position: relative;
}

.product-item:hover img {
	transform: scale(1.05);
}

/* Custom scroller styles */
.scroll-container {
	transition: transform 0.5s ease;
}

.image-scroller-button {
	background-color: rgba(255, 255, 255, 0.9);
	border: 1px solid rgba(0, 0, 0, 0.1);
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #333;
}

.image-scroller-button:hover {
	background-color: rgba(255, 255, 255, 1);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.image-scroller-button:disabled {
	opacity: 0.3;
	cursor: not-allowed;
}

/* Ensure consistent product heights */
.product-slide {
	display: flex;
	height: auto;
}

/* Handle width transitions smoothly */
@media (max-width: 639px) {
	.product-slide {
		transition: width 0.3s ease;
	}
}

@media (min-width: 640px) and (max-width: 767px) {
	.product-slide {
		transition: width 0.3s ease;
	}
}

@media (min-width: 768px) and (max-width: 1023px) {
	.product-slide {
		transition: width 0.3s ease;
	}
}

@media (min-width: 1024px) {
	.product-slide {
		transition: width 0.3s ease;
	}
}
</style>
